/**
 * ResumeAssessmentBanner — re-engagement ribbon for anonymous visitors who
 * have a saved (in-progress) lite self-assessment draft.
 *
 * Reads the lightweight marker `ekselens.lite.activeDraft` that LiteAssessment
 * writes while a draft is mid-flight (cleared on completion). Shows a one-line
 * "Continue your … assessment — N% complete →" ribbon, mirroring the SSO
 * re-entry banner's placement (fixed top, above the header).
 *
 * Priority: the SSO banner wins — if an active SSO session cookie is present
 * this banner stays hidden so the two ribbons never stack.
 */
import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { PlayCircle, X } from "lucide-react";

const MARKER_KEY = "ekselens.lite.activeDraft";
const DISMISS_KEY = "ekselens.lite.resumeDismissed";

const hasSsoCookie = () => {
  if (typeof document === "undefined") return false;
  return /(?:^|; )ek_sso=1(?:;|$)/.test(document.cookie);
};

const readDraft = () => {
  try {
    const raw = window.localStorage.getItem(MARKER_KEY);
    if (!raw) return null;
    const d = JSON.parse(raw);
    if (!d?.routeSlug || !d?.total || !d?.answered) return null;
    if (d.answered >= d.total) return null; // completed → nothing to resume
    return d;
  } catch {
    return null;
  }
};

export default function ResumeAssessmentBanner() {
  const location = useLocation();
  const [draft, setDraft] = useState(null);

  useEffect(() => {
    const path = location.pathname || "";
    // Never on admin, the assessment page itself, or while an SSO ribbon shows.
    if (path.startsWith("/admin") || path.includes("-self-assessment") || hasSsoCookie()) {
      setDraft(null);
      return;
    }
    let dismissed = false;
    try { dismissed = window.sessionStorage.getItem(DISMISS_KEY) === "1"; } catch {/*noop*/}
    if (dismissed) { setDraft(null); return; }
    setDraft(readDraft());
  }, [location.pathname]);

  useEffect(() => {
    const cls = "ek-has-top-banner";
    if (draft) document.body.classList.add(cls);
    else document.body.classList.remove(cls);
    return () => document.body.classList.remove(cls);
  }, [draft]);

  if (!draft) return null;

  const pct = Math.round((draft.answered / draft.total) * 100);
  const label = draft.subBrand || draft.title || "your";

  const dismiss = () => {
    setDraft(null);
    try { window.sessionStorage.setItem(DISMISS_KEY, "1"); } catch {/*noop*/}
  };

  return (
    <div
      className="fixed inset-x-0 top-0 z-[60] bg-[#7737F6] text-white h-10 flex items-center"
      role="status"
      data-testid="resume-assessment-banner"
    >
      <div className="ek-container flex items-center gap-3 text-[13px] w-full">
        <PlayCircle size={14} className="shrink-0 opacity-90" />
        <p className="flex-1 leading-snug">
          Continue your {label} self-assessment — {pct}% complete.{" "}
          <Link
            to={`/resources/${draft.routeSlug}`}
            className="underline underline-offset-2 font-medium hover:opacity-90"
            data-testid="resume-assessment-continue"
          >
            Pick up where you left off →
          </Link>
        </p>
        <button
          type="button"
          onClick={dismiss}
          className="shrink-0 opacity-80 hover:opacity-100"
          aria-label="Dismiss"
          data-testid="resume-assessment-dismiss"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}
